import {
  createContext, useContext, useEffect,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import type { TTagSize } from '@vlabs/uikit';
import { useWindowSize } from '@vlabs/uikit';
import type { TButtonSize } from '@vlabs/uikit/components/controls/button';

export type PageBreakpoint = 'large' | 'medium';
export type GetTitleComponent = (title: string) => React.ReactNode;
export type ButtonSize = TButtonSize;
export type InputSize = 'small' | 'medium';
export interface PageContext {
  breakpoint: PageBreakpoint;
  getTitleComponent: GetTitleComponent;
  buttonSize: ButtonSize;
  inputSize: InputSize;
  tagSize: TTagSize;
}

const BUTTON_SIZE_MAP: Record<PageBreakpoint, ButtonSize> = {
  large: 'medium',
  medium: 'small',
};
const INPUT_SIZE_MAP: Record<PageBreakpoint, InputSize> = {
  large: 'medium',
  medium: 'small',
};
const TAG_SIZE_MAP: Record<PageBreakpoint, TTagSize> = {
  large: 'large',
  medium: 'medium',
};

function getBreakpoint(windowWidth?: number): PageBreakpoint {
  if (!windowWidth || (windowWidth >= 1920)) {
    return 'large';
  }
  return 'medium';
}

const initialBreakpoint = getBreakpoint(window.innerWidth);

const defaultPageContext: PageContext = {
  breakpoint: initialBreakpoint,
  getTitleComponent: (title) => <h5 className="PageHeaderV2__Name">{title}</h5>,
  buttonSize: BUTTON_SIZE_MAP[initialBreakpoint],
  inputSize: INPUT_SIZE_MAP[initialBreakpoint],
  tagSize: TAG_SIZE_MAP[initialBreakpoint],
};
const PageCtx = createContext<PageContext>(defaultPageContext);

type PageProviderProps = PropsWithChildren;

function PageProvider({ children }: PageProviderProps): React.JSX.Element {
  const [ctx, setCtx] = useState<PageContext>(defaultPageContext);
  const windowSize = useWindowSize();

  useEffect(() => {
    const breakpoint = getBreakpoint(windowSize.width);

    setCtx((prevCtx) => {
      if (prevCtx.breakpoint === breakpoint) return prevCtx;
      const getTitleComponent: GetTitleComponent = (title) => {
        if (breakpoint === 'large') return <h5 className="PageHeaderV2__Name">{title}</h5>;
        return <h6 className="PageHeaderV2__Name">{title}</h6>;
      };

      return {
        breakpoint,
        getTitleComponent,
        buttonSize: BUTTON_SIZE_MAP[breakpoint],
        inputSize: INPUT_SIZE_MAP[breakpoint],
        tagSize: TAG_SIZE_MAP[breakpoint],
      };
    });
  }, [windowSize]);

  return (
    <PageCtx.Provider value={ctx}>
      {children}
    </PageCtx.Provider>
  );
}
const usePageContext = (): PageContext => useContext(PageCtx);

export { PageProvider, usePageContext };
